import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { StatutoryRegister, StatutoryTabs } from "@/components/StatutoryRegister";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MapPin, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function VillageAreas() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ ward: "", area_name: "", description: "", landmark: "" });
  const [regTab, setRegTab] = useState("module");

  const load = () => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/village-areas", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/village-areas", { panchayat_id: user?.panchayat_id, ...form });
      toast.success("Area added");
      setShow(false); setForm({ ward: "", area_name: "", description: "", landmark: "" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete area?")) return;
    await api.delete(`/village-areas/${id}`);
    load();
  };

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Masters</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-7 h-7 text-[#0A3161]" />
              {lang === "hi" ? "गाँव क्षेत्र मास्टर" : "Village Area Master"}
            </h1>
          </div>
          {regTab === "module" && (
          <button onClick={() => setShow(true)} data-testid="va-add-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
            <Plus className="w-4 h-4" /> {lang === "hi" ? "नया क्षेत्र" : "Add Area"}
          </button>
          )}
        </div>

        <StatutoryTabs moduleLabel={lang === "hi" ? "क्षेत्र" : "Areas"} codes={["VIII", "X", "XI", "XII"]} active={regTab} onChange={setRegTab} />
        {regTab !== "module" && <StatutoryRegister code={regTab} />}

        <div className={regTab === "module" ? "" : "hidden"}>
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="va-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr><th className="text-left px-4 py-3">Ward</th><th className="text-left px-4 py-3">Area Name</th>
                  <th className="text-left px-4 py-3">Landmark</th><th className="text-left px-4 py-3">Description</th>
                  <th className="text-right px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">{a.ward}</td>
                  <td className="px-4 py-3 font-medium">{a.area_name}</td>
                  <td className="px-4 py-3 text-xs">{a.landmark || "—"}</td>
                  <td className="px-4 py-3 text-xs">{a.description || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(a.id)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No areas defined</td></tr>}
            </tbody>
          </table>
        </div>
        </div>

        {show && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-md w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="font-heading text-xl font-bold">Add Village Area</h2>
                <button onClick={() => setShow(false)} className="p-2 text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submit} className="p-5 space-y-3 text-sm">
                <Field label="Ward"><input required value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} className="form-input" data-testid="va-ward" /></Field>
                <Field label="Area Name"><input required value={form.area_name} onChange={(e) => setForm({ ...form, area_name: e.target.value })} className="form-input" data-testid="va-name" /></Field>
                <Field label="Landmark"><input value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} className="form-input" /></Field>
                <Field label="Description"><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="form-input" /></Field>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300">Cancel</button>
                  <button type="submit" data-testid="va-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <style>{`.form-input{width:100%;padding:0.55rem 0.75rem;border-radius:0.375rem;border:1px solid #CBD5E1;background:white;font-size:0.875rem;outline:none}.form-input:focus{border-color:#0A3161;box-shadow:0 0 0 3px rgba(10,49,97,0.08)}`}</style>
    </OfficerLayout>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">{label}</div>{children}</div>
);
